import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { config } from '../config.js';
import { decodeJwtPayload, generatePkce, generateRandom } from './pkce.js';

export interface AuthUser {
  sub: string;
  email: string;
  name?: string;
}

export interface AuthContextValue {
  user: AuthUser | null;
  idToken: string | null;
  isLoading: boolean;
  signIn: () => Promise<void>;
  signOut: () => void;
  handleCallback: (code: string, state: string) => Promise<void>;
  refreshTokens: () => Promise<string | null>;
  registerFormState: (key: string, getValue: () => unknown) => void;
  unregisterFormState: (key: string) => void;
  getRestoredFormState: (key: string) => unknown;
}

const STORAGE = {
  pkceVerifier: 'auth_pkce_verifier',
  state: 'auth_state',
  nonce: 'auth_nonce',
  refreshToken: 'auth_refresh_token',
  formState: 'auth_form_state',
} as const;

interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

function tokenUrl(): string {
  return `https://${config.cognitoDomain}/oauth2/token`;
}

function callbackUrl(): string {
  return `${config.appUrl}/auth/callback`;
}

/* eslint-disable @typescript-eslint/no-empty-function */
const defaultCtx: AuthContextValue = {
  user: null,
  idToken: null,
  isLoading: false,
  signIn: async () => {},
  signOut: () => {},
  handleCallback: async () => {},
  refreshTokens: async () => null,
  registerFormState: () => {},
  unregisterFormState: () => {},
  getRestoredFormState: () => undefined,
};
/* eslint-enable @typescript-eslint/no-empty-function */

const AuthContext = createContext<AuthContextValue>(defaultCtx);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formStateRegistry = useRef<Map<string, () => unknown>>(new Map());

  // Load restored form state synchronously so child useState initializers can read it
  const [restoredFormState] = useState<Record<string, unknown>>(() => {
    const raw = localStorage.getItem(STORAGE.formState);
    if (!raw) return {};
    try {
      const result = JSON.parse(raw) as Record<string, unknown>;
      localStorage.removeItem(STORAGE.formState);
      return result;
    } catch {
      return {};
    }
  });

  const scheduleRefresh = useCallback((expiresInSeconds: number) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const delay = expiresInSeconds * 0.8 * 1000;
    refreshTimerRef.current = setTimeout(() => {
      void doRefresh();
    }, delay);
  }, []); // intentional: stable ref-based callbacks; no reactive deps needed

  const applyTokens = useCallback(
    (tokens: TokenResponse) => {
      const payload = decodeJwtPayload(tokens.id_token);
      const rawName = payload['name'];
      const rawUsername = payload['cognito:username'];
      const displayName =
        typeof rawName === 'string'
          ? rawName
          : typeof rawUsername === 'string'
            ? rawUsername
            : undefined;

      const authUser: AuthUser = {
        sub: typeof payload['sub'] === 'string' ? payload['sub'] : '',
        email: typeof payload['email'] === 'string' ? payload['email'] : '',
        ...(displayName !== undefined ? { name: displayName } : {}),
      };

      setUser(authUser);
      setIdToken(tokens.id_token);
      if (tokens.refresh_token) {
        sessionStorage.setItem(STORAGE.refreshToken, tokens.refresh_token);
      }
      scheduleRefresh(tokens.expires_in);
      return tokens.id_token;
    },
    [scheduleRefresh],
  );

  const clearAuth = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    setUser(null);
    setIdToken(null);
    sessionStorage.removeItem(STORAGE.refreshToken);
  }, []);

  const doRefresh = useCallback(async (): Promise<string | null> => {
    const storedRefreshToken = sessionStorage.getItem(STORAGE.refreshToken);
    if (!storedRefreshToken) return null;
    try {
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: config.cognitoClientId,
        refresh_token: storedRefreshToken,
      });
      const response = await fetch(tokenUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!response.ok) {
        clearAuth();
        return null;
      }
      const tokens = (await response.json()) as TokenResponse;
      return applyTokens(tokens);
    } catch {
      clearAuth();
      return null;
    }
  }, [applyTokens, clearAuth]);

  // Attempt silent refresh on mount if refresh token is present
  useEffect(() => {
    const storedRefreshToken = sessionStorage.getItem(STORAGE.refreshToken);
    if (storedRefreshToken) {
      doRefresh().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []); // intentional: stable ref-based callbacks; no reactive deps needed

  const signIn = useCallback(async () => {
    const { verifier, challenge } = await generatePkce();
    const state = generateRandom();
    const nonce = generateRandom();

    sessionStorage.setItem(STORAGE.pkceVerifier, verifier);
    sessionStorage.setItem(STORAGE.state, state);
    sessionStorage.setItem(STORAGE.nonce, nonce);

    // Preserve in-flight form state across the redirect
    const savedFormState: Record<string, unknown> = {};
    formStateRegistry.current.forEach((getValue, key) => {
      savedFormState[key] = getValue();
    });
    if (Object.keys(savedFormState).length > 0) {
      localStorage.setItem(STORAGE.formState, JSON.stringify(savedFormState));
    }

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: config.cognitoClientId,
      redirect_uri: callbackUrl(),
      scope: 'openid profile email',
      code_challenge_method: 'S256',
      code_challenge: challenge,
      state,
      nonce,
    });

    // Skip the Cognito Hosted-UI identity-provider chooser where the environment pins one
    // (dev/prod → Enterprise Auth). Left unset, the chooser is shown so a direct Cognito
    // account is equally reachable — the sandbox/local-dev behaviour. The Cognito password
    // form stays reachable in every environment via an explicit
    // `&identity_provider=COGNITO` authorize URL, which is the break-glass and e2e path.
    if (config.signInIdentityProvider) {
      params.set('identity_provider', config.signInIdentityProvider);
    }

    window.location.assign(`https://${config.cognitoDomain}/oauth2/authorize?${params.toString()}`);
  }, []);

  const handleCallback = useCallback(
    async (code: string, returnedState: string) => {
      const storedState = sessionStorage.getItem(STORAGE.state);

      if (returnedState !== storedState) {
        sessionStorage.removeItem(STORAGE.state);
        throw new Error('State mismatch — possible CSRF attack');
      }

      const verifier = sessionStorage.getItem(STORAGE.pkceVerifier);
      const nonce = sessionStorage.getItem(STORAGE.nonce);

      sessionStorage.removeItem(STORAGE.state);
      sessionStorage.removeItem(STORAGE.pkceVerifier);
      sessionStorage.removeItem(STORAGE.nonce);

      if (!verifier) throw new Error('Missing PKCE verifier');

      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: config.cognitoClientId,
        code,
        redirect_uri: callbackUrl(),
        code_verifier: verifier,
      });

      const response = await fetch(tokenUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (!response.ok) throw new Error(`Token exchange failed: ${response.status}`);

      const tokens = (await response.json()) as TokenResponse;

      const payload = decodeJwtPayload(tokens.id_token);
      if (nonce && payload['nonce'] !== nonce) {
        throw new Error('Nonce mismatch — possible replay attack');
      }

      applyTokens(tokens);
    },
    [applyTokens],
  );

  const signOut = useCallback(() => {
    clearAuth();
    const params = new URLSearchParams({
      client_id: config.cognitoClientId,
      logout_uri: config.appUrl,
    });
    window.location.assign(`https://${config.cognitoDomain}/logout?${params.toString()}`);
  }, [clearAuth]);

  const registerFormState = useCallback((key: string, getValue: () => unknown) => {
    formStateRegistry.current.set(key, getValue);
  }, []);

  const unregisterFormState = useCallback((key: string) => {
    formStateRegistry.current.delete(key);
  }, []);

  const getRestoredFormState = useCallback(
    (key: string): unknown => restoredFormState[key],
    [restoredFormState],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      idToken,
      isLoading,
      signIn,
      signOut,
      handleCallback,
      refreshTokens: doRefresh,
      registerFormState,
      unregisterFormState,
      getRestoredFormState,
    }),
    [
      user,
      idToken,
      isLoading,
      signIn,
      signOut,
      handleCallback,
      doRefresh,
      registerFormState,
      unregisterFormState,
      getRestoredFormState,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
